import React, { FC, useEffect } from "react";
import { 
  Modal, 
  Button, 
  Typography, 
  Space, 
  Card, 
  Row, 
  Col, 
  Tag,
  message
} from "antd";
import { 
  CheckCircleOutlined, 
  DownloadOutlined,
  FileTextOutlined,
  TrophyOutlined,
  BarChartOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { AnalysisResults } from '../utils/types';
import { BaseUrl } from '../utils/network';

const { Title, Text } = Typography;

interface AnalysisResultsModalProps {
  visible: boolean;
  onClose: () => void;
  analysisResults: AnalysisResults;
  caseNumber: number;
  pdfUrl?: string;
  pdfFilename?: string;
}

const AnalysisResultsModal: FC<AnalysisResultsModalProps> = ({
  visible,
  onClose,
  analysisResults,
  caseNumber,
  pdfUrl,
  pdfFilename
}) => {
  const navigate = useNavigate();
  
  // Debug logging
  console.log('AnalysisResultsModal - pdfUrl:', pdfUrl);
  console.log('AnalysisResultsModal - pdfFilename:', pdfFilename);
  
  const getLikelihoodColor = (likelihood: string) => {
    switch (likelihood) {
      case 'High':
        return '#52c41a';
      case 'Moderate':
        return '#faad14';
      case 'Low':
        return '#ff4d4f';
      default:
        return '#1890ff';
    }
  };

  const getLikelihoodIcon = (likelihood: string) => {
    switch (likelihood) {
      case 'High':
        return <TrophyOutlined style={{ color: '#52c41a' }} />;
      case 'Moderate':
        return <BarChartOutlined style={{ color: '#faad14' }} />;
      case 'Low':
        return <BarChartOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return <BarChartOutlined style={{ color: '#1890ff' }} />;
    }
  };

  const handleDownloadPDF = async () => {
    if (!pdfUrl) {
      message.error('PDF report is not available');
      return;
    }

    try {
      // Get the base URL from network config
      const baseUrl = BaseUrl.replace('/api/', ''); // Remove /api/ from base URL
      const fullUrl = pdfUrl.startsWith('http') ? pdfUrl : `${baseUrl}${pdfUrl}`;
      
      // Fetch the file with proper headers
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('employeepro_token')}` || '',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the blob
      const blob = await response.blob();
      
      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = pdfFilename || `case_${caseNumber}_analysis.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      window.URL.revokeObjectURL(downloadUrl);
      
      message.success('PDF report downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      message.error('Failed to download PDF report. Please try again.');
    }
  };

  // Auto-download PDF when modal becomes visible
  useEffect(() => {
    if (visible && pdfUrl) {
      // Trigger download automatically after a short delay
      const timer = setTimeout(() => {
        handleDownloadPDF();
      }, 500);
      
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, pdfUrl]);

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
          <Button 
            key="new-interview" 
            icon={<PlusOutlined />}
            onClick={() => {
              onClose();
              // Navigate to wizard for new interview
              navigate('/create-case-wizard');
            }}
          >
            Begin New Interview
          </Button>
          {pdfUrl && (
            <Button 
              key="download" 
              icon={<DownloadOutlined />}
              onClick={handleDownloadPDF}
            >
              Download Report
            </Button>
          )}
          <Button key="close" type="primary" onClick={onClose}>
            Close
          </Button>
        </div>
      }
      width={600}
      centered
      closable={false}
      maskClosable={false}
      title={null}
      bodyStyle={{ padding: '12px' }}
    >
      <div style={{ textAlign: 'center', padding: '8px 0' }}>
        {/* Success Icon */}
        <CheckCircleOutlined 
          style={{ 
            fontSize: '40px', 
            color: '#52c41a', 
            marginBottom: '8px',
            display: 'block'
          }} 
        />
        
        {/* Title */}
        <Title level={5} style={{ color: '#52c41a', margin: '0 0 6px 0' }}>
          Analysis Complete!
        </Title>
        
        {/* Case Number */}
        <Text style={{ fontSize: '13px', color: '#595959', display: 'block', marginBottom: '12px' }}>
          Case #{caseNumber} has been analyzed
        </Text>

        {/* Main Analysis Result Card */}
        <Card 
          style={{ 
            marginBottom: '12px',
            backgroundColor: getLikelihoodColor(analysisResults.likelihood) + '10',
            border: `2px solid ${getLikelihoodColor(analysisResults.likelihood)}`,
            borderRadius: '6px'
          }}
          bodyStyle={{ padding: '10px' }}
        >
          <Row gutter={12} align="middle">
            <Col span={6}>
              <div style={{ fontSize: '32px', textAlign: 'center' }}>
                {getLikelihoodIcon(analysisResults.likelihood)}
              </div>
            </Col>
            <Col span={18}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div>
                  <Text style={{ fontSize: '15px', fontWeight: 'bold', color: '#262626' }}>
                    Likelihood: {analysisResults.likelihood}
                  </Text>
                  <Tag 
                    color={getLikelihoodColor(analysisResults.likelihood)}
                    style={{ marginLeft: '6px', fontSize: '11px', fontWeight: 'bold' }}
                  >
                    {(analysisResults.confidence_score * 100).toFixed(0)}%
                  </Tag>
                </div>
                <Text style={{ fontSize: '12px', color: '#595959' }}>
                  {analysisResults.case_ref}
                </Text>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Analysis Details */}
        <Card style={{ marginBottom: '12px', textAlign: 'left' }} bodyStyle={{ padding: '10px' }}>
          <div style={{ 
            backgroundColor: '#f6ffed', 
            border: '1px solid #b7eb8f',
            borderRadius: '4px',
            padding: '10px'
          }}>
            <Text style={{ fontSize: '12px', color: '#389e0d', lineHeight: '1.45' }}>
              <strong>Reasoning:</strong><br/>
              {analysisResults.reasoning}
            </Text>
          </div>
        </Card>

        {/* PDF Report Section */}
        {pdfUrl && (
          <Card style={{ marginBottom: '8px', textAlign: 'left' }} bodyStyle={{ padding: '8px' }}>
            <Row align="middle" gutter={10}>
              <Col>
                <FileTextOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
              </Col>
              <Col flex="auto">
                <div>
                  <Text strong style={{ fontSize: '12px' }}>Detailed Report Available</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '10px' }}>
                    {pdfFilename || `case_${caseNumber}_analysis.pdf`}
                  </Text>
                </div>
              </Col>
            </Row>
          </Card>
        )}

        {/* Footer Note */}
        <Text style={{ fontSize: '10px', color: '#8c8c8c', display: 'block', marginTop: '8px' }}>
          Analysis based on historical EEOC data. For informational purposes only.
        </Text>
      </div>
    </Modal>
  );
};

export default AnalysisResultsModal;
